import { getQueryJson, localStoragePayhelm, getDefaultFilterType, validateJSONString, } from 'modules/utils'
import moment from 'momentExt'
import { dateTypes, } from 'modules/constants'

let dateFilters = {}
const savedState = localStoragePayhelm.dateFilters && validateJSONString(localStoragePayhelm.dateFilters)
  ? JSON.parse(localStoragePayhelm.dateFilters) : undefined

// Check if saved state exists and has valid data
let hasValidSavedState = false
let hasActiveFilter = false

if (savedState) {
  hasValidSavedState = true

  // Check for any new date types that might have been added
  const missingDateTypes = dateTypes.filter(dateType => !Object.keys(savedState).includes(dateType))

  // If there are new date types, we need to add them to savedState
  if (missingDateTypes.length > 0) {
    missingDateTypes.forEach(dateType => {
      savedState[dateType] = {
        active: false, // New types are inactive by default
        dateRange: { start: moment().format('YYYY-MM-DD'), end: moment().format('YYYY-MM-DD') },
        compareDateRange: { start: moment().format('YYYY-MM-DD'), end: moment().format('YYYY-MM-DD') },
      }
    })

    // Update localStorage with the new date types
    localStoragePayhelm.dateFilters = JSON.stringify(savedState)
  }

  // Now validate all existing entries
  for (const dateType of Object.keys(savedState)) {
    // Check if this is a valid date type
    if (!dateTypes.includes(dateType)) {
      hasValidSavedState = false
      break
    }

    // Check if date ranges are valid
    if (!savedState[dateType]?.dateRange?.start || !savedState[dateType]?.dateRange?.end) {
      hasValidSavedState = false
      break
    }

    // Check if at least one filter is active
    if (savedState[dateType]?.active === true) {
      hasActiveFilter = true
    }
  }
}

// Use saved state if valid and has at least one active filter, otherwise create new
if (hasValidSavedState && hasActiveFilter) {
  dateFilters = savedState
} else {
  // Initialize with defaults
  dateTypes.forEach(dateType => {
    dateFilters[dateType] = {
      active: dateType === dateTypes[0] ? true : false,
      dateRange: { start: moment().format('YYYY-MM-DD'), end: moment().format('YYYY-MM-DD') },
      compareDateRange: { start: moment().format('YYYY-MM-DD'), end: moment().format('YYYY-MM-DD') },
    }
  })
  localStoragePayhelm.dateFilters = JSON.stringify(dateFilters)
}

const queryJson = getQueryJson()
try {
  for (const type of dateTypes) {
    let active = false
    let dateRange = undefined
    let compareDateRange = undefined
    if (queryJson[type] !== undefined) {
      const [start, end] = queryJson[type].split('to')
      if (moment(start).isValid() && moment(end).isValid()) {
        if (moment(start) <= moment(end)) {
          active = true
          dateRange = { start: moment(start).format('YYYY-MM-DD'), end: moment(end).format('YYYY-MM-DD'), }
        } else {
          active = true
          dateRange = { start: moment(end).format('YYYY-MM-DD'), end: moment(start).format('YYYY-MM-DD'), }
        }
      }
    }
    if (queryJson[`compare-${type}`] !== undefined) {
      const [compareStart, compareEnd] = queryJson[`compare-${type}`].split('to')
      if (moment(compareStart).isValid() && moment(compareEnd).isValid()) {
        if (moment(compareStart) <= moment(compareEnd)) {
          active = true
          compareDateRange = { start: moment(compareStart).format('YYYY-MM-DD'), end: moment(compareEnd).format('YYYY-MM-DD'), }
        } else {
          active = true
          compareDateRange = { start: moment(compareEnd).format('YYYY-MM-DD'), end: moment(compareStart).format('YYYY-MM-DD'), }
        }
      }
    }
    if (active && dateRange && compareDateRange) dateFilters[type] = { active, dateRange, compareDateRange, }
    else if (active && dateRange) dateFilters[type] = { active, dateRange, }
  }
} catch (error) {
  console.log(error)
}

const initialState = {
  dateTypes: dateFilters,
  prevDateType: null,
  prevSearch: null,
  updated: false,
}

export default function reducer(state = initialState, action) {
  let nextState = state

  switch(action.type) {
    case 'ACTIVATE_DATE_TYPE': {
      nextState = {
        ...nextState,
        dateTypes: {
          ...nextState.dateTypes,
          [action.payload]: {
            ...nextState.dateTypes[action.payload],
            active: true,
          },
        },
        prevDateType: action.payload,
      }
      break
    }
    case 'DEACTIVATE_DATE_TYPE': {
      nextState = {
        ...nextState,
        dateTypes: {
          ...nextState.dateTypes,
          [action.payload]: {
            ...nextState.dateTypes[action.payload],
            active: false,
            dateRange: {
              start: null,
              end: null,
            },
          },
        },
      }
      nextState = {
        ...nextState,
        prevDateType: getDefaultFilterType(nextState.dateTypes).defaultFilterType,
      }
      break
    }
    case 'CHANGE_DATE_TYPE': {
      nextState = {
        ...state,
        dateTypes: {
          ...state.dateTypes,
          [action.payload.prevDateType]: {
            ...state.dateTypes[action.payload.nextDateType],
          },
          [action.payload.nextDateType]: {
            ...state.dateTypes[action.payload.prevDateType],
          },
        }
      }
      break
    }
    case 'DEACTIVE_ALL_DATE_TYPES': {
      let preserveActive
      let nextDateTypes = {}
      for (const dateType of Object.keys(nextState.dateTypes)) {
        if (nextState.dateTypes[dateType].active && !preserveActive) { preserveActive = dateType }
        nextDateTypes[dateType] = {
          ...nextState.dateTypes[dateType],
          active: preserveActive === dateType ? true : false,
          dateRange:
            preserveActive === dateType
              ? nextState.dateTypes[dateType].dateRange
              : {
                start: null,
                end: null,
              },
        }
      }
      nextState = {
        ...nextState,
        dateTypes: nextDateTypes,
        prevDateType: getDefaultFilterType(nextDateTypes).defaultFilterType,
      }
      break
    }
    case 'SET_DATE_RANGE': {
      nextState = {
        ...nextState,
        dateTypes: {
          ...nextState.dateTypes,
          [action.payload.dateType]: {
            ...nextState.dateTypes[action.payload.dateType],
            dateRange: action.payload.dateRange,
          },
        },
        prevDateType: action.payload.dateType,
      }
      break
    }
    case 'SET_COMPARE_DATE_RANGE': {
      nextState = {
        ...nextState,
        dateTypes: {
          ...nextState.dateTypes,
          [action.payload.dateType]: {
            ...nextState.dateTypes[action.payload.dateType],
            compareDateRange: action.payload.compareDateRange,
          },
        },
        prevDateType: action.payload.dateType,
      }
      break
    }
    case 'SET_PREVIOUS_SEARCH': {
      nextState = {
        ...nextState,
        prevSearch: nextState.dateTypes,
      }
      break
    }
    case 'SET_UPDATED': {
      nextState = {
        ...nextState,
        updated: true,
      }
      break
    }
    case 'UNSET_UPDATED': {
      nextState = {
        ...nextState,
        updated: false,
      }
      break
    }
    default:
      break
  }
  try {
    localStoragePayhelm.dateFilters = JSON.stringify(nextState.dateTypes || '')
  } catch (e) {
    console.log(e)
  }
  return nextState
}
